/**
 * @fileoverview This file contains the form for movement creation
 *
 * @version 1.0.0
 * @since 1.0.0
 * @author Santiago Cano Herrera
 */

//#region Imports
import moment from 'moment';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faMinus } from '@fortawesome/pro-light-svg-icons';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  // Checkbox,
  Skeleton,
  CircularProgress,
  createFilterOptions,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CurrencyInput from '../UI/CurrencyInput';
import { validateAllFields } from '../../utils/validateObjectValues';
import { isValueInArray, trimValue } from '../../utils/generalUtils';

import customAxios from '../../utils/customAxios';
import { GlobalVariables } from '../../globalVar';
import variables from '../../scss/_variables.scss';
import '../../scss/Components/Inventory/_movementmodal.scss';
import { ToggleOption, ToggleSwitch } from '../UI/ToggleSwitch';
import { getToast } from '../../utils/toastConfig';
import { movementsArrayAtom } from '../../stores/movementsStore';
//#endregion

const filter = createFilterOptions();

const stylesCSS = {
  input: {
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
};

const MovementForm = ({
  itemId,
  itemName,
  onHide,
  warehouseId,
  warehouseName,
  locationId,
  locationName,
  refreshItems,
}) => {
  //#region State variables
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [inventoryId, setInventoryId] = useState(warehouseId);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  // const [isFutureMovement, setIsFutureMovement] = useState(false);
  const [inputTypes, setInputTypes] = useState([]);
  const [outputTypes, setOutputTypes] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorName, setSupervisorName] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [inventories, setInventories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [movement, setMovement] = useState({
    // date: moment(),
    locationId: locationId,
    quantity: 0,
    supervisorId: null,
    receiverId: null,
    type: null,
    warehouse: warehouseId,
    provider: '',
    notes: '',
    reason: '',
    isInput: isActive,
  });
  //#endregion

  // eslint-disable-next-line no-unused-vars
  const [movements, setMovements] = useAtom(movementsArrayAtom);
  //#region LOCATION AUTOCOMPLETE
  const [open, setOpen] = useState(false);
  const initialDialogValue = useState({
    id: '',
    inventory_type_id: '',
    rack: '',
  });
  const [dialogValue, setDialogValue] = useState(initialDialogValue);

  const handleCloseDialog = () => {
    setDialogValue(initialDialogValue);
    setOpen(false);
  };

  const handleSubmitDialog = async (e) => {
    e.preventDefault();
    try {
      const createLocationRes = await customAxios.post(
        `${GlobalVariables.getURL()}locations/create`,
        {
          inventory_type_id: movement.warehouse,
          rack: dialogValue.rack,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setMovement((prev) => ({
        ...prev,
        locationId: createLocationRes.data.insertId,
      }));

      await fetchLocations();
    } catch (error) {
      console.log(error);
      getToast({ status: 'error', title: error.response.data.message });
      setMovement((prev) => ({ ...prev, locationId: '' }));
    }

    handleCloseDialog();
  };

  //#endregion

  /**
   * @summary Fetches the list of technicians from the endpoint.
   * @since 1.0.0
   *
   * @function fetchSupervisors
   * @async
   * @returns {Promise<Object[]>} The list of technicians.
   */
  const fetchSupervisors = async () => {
    const endpoint = `${GlobalVariables.getURL()}roles/users?role=admin`;
    const response = await customAxios.get(endpoint, {
      headers: {
        Authorization: 'bearer ' + localStorage.getItem('token'),
      },
    });
    return response.data.users;
  };

  const fetchMovementTypes = async () => {
    const endpoint = `${GlobalVariables.getURL()}movements/types`;
    const response = await customAxios.get(endpoint, {
      headers: {
        Authorization: 'bearer ' + localStorage.getItem('token'),
      },
    });
    return response.data.movementTypes;
  };

  const fetchInventories = async () => {
    const endpoint = `${GlobalVariables.getURL()}inventory/getInventoryTypesList`;
    const response = await customAxios.get(endpoint, {
      headers: {
        Authorization: 'bearer ' + localStorage.getItem('token'),
      },
    });
    return response.data.data;
  };

  const fetchLocations = async () => {
    setIsLoadingLocations(true);
    if (inventoryId) {
      try {
        const endpoint = `${GlobalVariables.getURL()}locations/getLocationsByInventoryType/${inventoryId}`;
        const response = await customAxios.get(endpoint, {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        });
        setLocations(response.data.data || []);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
        getToast({ status: 'error', title: error.response.data.message });
      } finally {
        setIsLoadingLocations(false);
      }
    }
  };

  /**
   * @summary Fetches data and updates the technicians state.
   * @since 1.0.0
   *
   * @function fetchData
   * @async
   * @returns {Promise<void>}
   */
  const fetchData = async () => {
    setIsLoadingData(true);
    try {
      const [supervisorsData, movementTypesData, inventoriesData] =
        await Promise.all([
          fetchSupervisors(),
          fetchMovementTypes(),
          fetchInventories(),
        ]);

      const inputTypes = movementTypesData.filter(
        (type) => type.io_type === 'INPUT'
      );
      const outputTypes = movementTypesData.filter(
        (type) => type.io_type === 'OUTPUT'
      );
      const supervisorFormatted = supervisorsData.map((supervisor) => ({
        ...supervisor,
        name: supervisor.name + ' ' + supervisor.last_name,
      }));

      setInputTypes(inputTypes);
      setOutputTypes(outputTypes);
      setInventories(inventoriesData);
      setSupervisors(supervisorFormatted);
    } catch (error) {
      console.log('🚀 ~ error:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  //#region Use effect hooks
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMovement({
      // date: moment(),
      locationId: locationId,
      quantity: 0,
      supervisorId: null,
      receiverId: null,
      type: isActive ? 1 : 5,
      warehouse: warehouseId,
      provider: '',
      notes: '',
      reason: '',
      isInput: isActive,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId]);
  //#endregion

  const resetFormFields = () => {
    setMovement({
      // date: moment(),
      locationId: '',
      locationName: '',
      quantity: 0,
      supervisorId: null,
      receiverId: null,
      type: isActive ? 1 : 5,
      warehouse: null,
      warehouseName: '',
      provider: '',
      notes: '',
      reason: '',
      isInput: isActive,
    });
    setSupervisorName('');
    setReceiverName('');
  };

  /**
   * @summary Handles the change event for form inputs and updates the movement state.
   * @since 1.0.0
   *
   * @function handleChange
   * @param {Event} event - The change event triggered by an input.
   */
  const handleChange = (event) => {
    const { name, value } = event.target;

    setMovement({
      ...movement,
      [name]: value,
    });
  };

  const handleMultipleMovements = () => {
    const type = movement.isInput
      ? inputTypes.filter((type) => type.id === movement.type)
      : outputTypes.filter((type) => type.id === movement.type);

    setMovements((prev) => [
      ...prev,
      {
        ...movement,
        itemId,
        itemName,
        // date: isFutureMovement ? movement.date : null,
        warehouseName,
        locationName,
        typeName: type[0].name,
        provider: movement.isInput ? movement.provider : null,
        supervisorName,
        receiverName: !movement.isInput ? receiverName : null,
      },
    ]);

    getToast({ status: 'success', title: 'Orden agregada al carrito.' });

    resetFormFields();
    onHide(false);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const movementInfo = {
      ...movement,
      itemId,
      itemName,
      // date: isFutureMovement ? movement.date : null,
    };

    try {
      const response = await customAxios.post(
        `${GlobalVariables.getURL()}movements/create`,
        movementInfo,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Movimiento creado correctamente',
        });
        resetFormFields();
        onHide(false);
        refreshItems();
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      getToast({ status: 'error', title: error.response.data.message });
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      adapterLocale={moment.locale()}
    >
      <Box
        component='form'
        height='100%'
        id='form-movement'
        onSubmit={handleFormSubmit}
      >
        <Grid container spacing={2} className='movement-form-row-text'>
          <Grid item xs={12}>
            <Typography className='movement-form-title'>
              Crear un nuevo movimiento...
            </Typography>
          </Grid>

          <Grid item container xs={12} display='flex' alignItems='center'>
            <Grid item xs={3}>
              <Typography>¿Es entrada o salida?</Typography>
            </Grid>
            <Grid item xs>
              <Divider flexItem sx={{ marginRight: '20px' }} />
            </Grid>
            <Grid
              item
              xs={4}
              display='flex'
              justifyContent='end'
              alignItems='center'
            >
              <ToggleSwitch isActive={isActive} onClick={setIsActive}>
                <ToggleOption>Entrada</ToggleOption>
                <ToggleOption>Salida</ToggleOption>
              </ToggleSwitch>
            </Grid>
          </Grid>

          <Grid item container xs={12} display='flex' alignItems='center'>
            <Grid item xs={3}>
              <Typography>Tipo de movimiento</Typography>
            </Grid>
            <Grid item xs>
              <Divider flexItem sx={{ marginRight: '20px' }} />
            </Grid>
            <Grid item xs={4}>
              {isLoadingData ? (
                <FormControl fullWidth>
                  <Skeleton variant='rounded' height={56} />
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id='mov-type-label'>
                    Tipo de movimiento
                  </InputLabel>
                  <Select
                    labelId='mov-type-label'
                    label='Tipo de movimiento'
                    name='type'
                    value={movement.type}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                  >
                    {isActive
                      ? inputTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))
                      : outputTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>

          <Grid item container xs={12} display='flex' alignItems='center'>
            <Grid item xs={2}>
              <Typography>Cantidad</Typography>
            </Grid>
            <Grid item xs>
              <Divider flexItem sx={{ marginRight: '20px' }} />
            </Grid>
            <Grid
              item
              xs={4}
              display='flex'
              justifyContent='end'
              alignItems='center'
            >
              <Button
                variant='contained'
                disableRipple
                onClick={() =>
                  setMovement((prev) => ({
                    ...prev,
                    quantity: parseInt(prev.quantity) - 1,
                  }))
                }
                sx={{
                  padding: '12px 12px',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                    backgroundColor: variables.redDabi,
                    opacity: 0.9,
                  },
                  backgroundColor: variables.redDabi,
                }}
              >
                <FontAwesomeIcon icon={faMinus} />
              </Button>
              <TextField
                value={movement.quantity}
                onChange={handleChange}
                name='quantity'
                type='number'
                sx={{
                  marginLeft: '5%',
                  marginRight: '5%',
                  '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': {
                    textAlign: 'center',
                  },
                  ...stylesCSS.input,
                }}
                InputProps={{
                  sx: { height: '40px' },
                }}
              />
              <Button
                variant='contained'
                disableRipple
                className='movement-form-quantity-button'
                onClick={() =>
                  setMovement((prev) => ({
                    ...prev,
                    quantity: parseInt(prev.quantity) + 1,
                  }))
                }
                sx={{
                  padding: '12px 12px',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                    backgroundColor: variables.redDabi,
                    opacity: 0.9,
                  },
                  backgroundColor: variables.redDabi,
                }}
              >
                <FontAwesomeIcon icon={faAdd} />
              </Button>
            </Grid>
          </Grid>

          {isActive && (
            <Grid item container xs={12} display='flex' alignItems='center'>
              <TextField
                required
                fullWidth
                label='Proveedor'
                value={movement.provider}
                name='provider'
                onChange={handleChange}
              />
            </Grid>
          )}

          {/* <Grid
            item
            xs={12}
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Grid item xs={3} display='flex' alignItems='center'>
              <Checkbox
                sx={{ marginRight: '5%' }}
                checked={isFutureMovement}
                onChange={() => setIsFutureMovement((prev) => !prev)}
              />
              <Typography width='200px'>Agendar orden</Typography>
            </Grid>
            <Grid item xs={4} display='flex' alignItems='center'>
              <Typography sx={{ marginRight: '5%' }}>Fecha</Typography>
              <DatePicker value={movement.date} disabled={!isFutureMovement} />
            </Grid>
          </Grid> */}

          <Grid
            item
            container
            xs={12}
            spacing={2}
            display='flex'
            alignItems='center'
          >
            <Grid item xs={6} display='flex' alignItems='center'>
              {isLoadingData ? (
                <FormControl fullWidth>
                  <Skeleton variant='rounded' height={56} />
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id='mov-warehouse-label'>Almacén</InputLabel>
                  <Select
                    labelId='mov-warehouse-label'
                    label='Almacén'
                    name='warehouse'
                    value={movement.warehouse}
                    onChange={(e) => {
                      handleChange(e);
                      setInventoryId(e.target.value);
                    }}
                  >
                    {inventories.map((inventory) => (
                      <MenuItem key={inventory.id} value={inventory.id}>
                        {inventory.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={6} display='flex' alignItems='center'>
              {isLoadingLocations ? (
                <FormControl fullWidth>
                  <Skeleton variant='rounded' height={56} />
                </FormControl>
              ) : (
                <>
                  <Autocomplete
                    value={
                      locations.find(
                        (location) => location.id === movement.locationId
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      if (typeof newValue === 'string') {
                        setTimeout(() => {
                          setOpen(true);
                          setDialogValue({
                            inventory_type_id: movement.warehouse,
                            rack: trimValue(newValue),
                          });
                        });
                      } else if (newValue && newValue.inputValue) {
                        setOpen(true);
                        setDialogValue({
                          inventory_type_id: movement.warehouse,
                          rack: trimValue(newValue.inputValue),
                        });
                      } else {
                        setMovement({
                          ...movement,
                          locationId: newValue ? newValue.id : null,
                        });
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const trimedValue = trimValue(params.inputValue);

                      if (
                        params.inputValue !== '' &&
                        !isValueInArray(trimedValue, locations, 'rack')
                      ) {
                        filtered.push({
                          inputValue: params.inputValue,
                          rack: `Agregar ${trimedValue}`,
                        });
                      }

                      return filtered;
                    }}
                    id='free-solo-dialog-demo'
                    options={locations}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      if (option.inputValue) return option.inputValue;
                      return option.rack;
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    renderOption={(props, option) => (
                      <li {...props}>{option.rack}</li>
                    )}
                    sx={{ width: '100%' }}
                    freeSolo
                    renderInput={(params) => (
                      <TextField {...params} label='Ubicación' required />
                    )}
                  />
                  <Dialog open={open} onClose={handleCloseDialog}>
                    <form>
                      <DialogTitle>Agregar nueva ubicación</DialogTitle>
                      <DialogContent>
                        <DialogContentText>
                          {`No se encontró la ubicación ${dialogValue.rack}. ¿Desea agregarla?`}
                        </DialogContentText>
                        <TextField
                          autoFocus
                          margin='dense'
                          id='rack'
                          value={dialogValue.rack}
                          onChange={(event) =>
                            setDialogValue({
                              ...dialogValue,
                              rack: event.target.value,
                            })
                          }
                          label='# de rack'
                          type='text'
                          variant='standard'
                        />
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancelar</Button>
                        <Button onClick={handleSubmitDialog}>Agregar</Button>
                      </DialogActions>
                    </form>
                  </Dialog>
                </>
              )}
            </Grid>
          </Grid>

          {isLoadingData ? (
            <Grid item xs={12} display='flex' alignItems='center'>
              <Skeleton variant='rounded' sx={{ width: '100%' }} height={56} />
            </Grid>
          ) : (
            <Grid item xs={12} display='flex' alignItems='center'>
              <Autocomplete
                disablePortal
                includeInputInList={false}
                options={supervisors}
                sx={{ width: '100%' }}
                value={
                  supervisors.find(
                    (tech) => tech.id === movement.supervisorId
                  ) || null
                }
                getOptionLabel={(tech) => tech.name}
                getOptionSelected={(supervisors, value) =>
                  supervisors.id === value.id
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Encargado de la orden'
                    required
                  />
                )}
                onChange={(event, newValue) => {
                  setMovement({
                    ...movement,
                    supervisorId: newValue ? newValue.id : null,
                  });
                  setSupervisorName(newValue ? newValue.name : null);
                }}
              />
            </Grid>
          )}

          {!isActive && (
            <Grid item xs={12} display='flex' alignItems='center'>
              <Autocomplete
                disablePortal
                includeInputInList={false}
                options={supervisors}
                sx={{ width: '100%' }}
                value={
                  supervisors.find((tech) => tech.id === movement.receiverId) ||
                  null
                }
                getOptionLabel={(tech) => tech.name}
                getOptionSelected={(supervisors, value) =>
                  supervisors.id === value.id
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label='Receptor' required />
                )}
                onChange={(event, newValue) => {
                  setMovement({
                    ...movement,
                    receiverId: newValue ? newValue.id : null,
                  });
                  setReceiverName(newValue ? newValue.name : null);
                }}
              />
            </Grid>
          )}

          <Grid
            item
            container
            xs={12}
            display='flex'
            alignItems='stretch'
            spacing={2}
          >
            <Grid item xs={12} sm={6} display='flex' flexDirection='column'>
              <Typography>Motivo del movimiento</Typography>
              <TextField
                fullWidth
                placeholder='Escriba el motivo de este movimiento...'
                name='reason'
                value={movement.reason}
                onChange={handleChange}
                multiline
                sx={{ flex: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} display='flex' flexDirection='column'>
              <Typography>Notas</Typography>
              <TextField
                fullWidth
                placeholder='Notas'
                name='notes'
                value={movement.notes}
                onChange={handleChange}
                multiline
                sx={{ flex: 1 }}
              />
            </Grid>
          </Grid>

          <Grid item container xs={12} display='flex' spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                variant='outlined'
                fullWidth
                sx={{
                  borderColor: variables.redDabi,
                  color: variables.redDabi,
                  '&:hover': {
                    borderColor: variables.redDabi,
                    backgroundColor: variables.redDabiHover,
                  },
                }}
                onClick={handleMultipleMovements}
              >
                Agregar a la orden
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} p>
              <Button
                variant='contained'
                fullWidth
                type='submit'
                form='form-movement'
                sx={{
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                    backgroundColor: variables.redDabi,
                    opacity: 0.7,
                  },
                  backgroundColor: variables.redDabi,
                }}
              >
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <CircularProgress size={24} color='inherit' />
                  </div>
                ) : (
                  'Ordenar ahora'
                )}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default MovementForm;
